import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Divider from 'shell/components/Divider';
import FormLabel from 'shell/components/FormLabel';
import Radio from 'shell/components/Radio';
import RadioGroup from 'shell/components/RadioGroup';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';
import ViewMore from 'shell/components/ViewMore';

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomAccordion = styled(Accordion)(({ theme, isGraded }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[400]}`,
        borderRadius: '8px !important',
        ...(isGraded && {
            borderColor: theme.palette.ensightful.success[700],
        }),
    };
});

const CustomAccordionSummary = styled(AccordionSummary)(({ theme, isExpanded, isGraded }) => {
    return {
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        borderRadius: isExpanded ? '8px 8px 0 0 !important' : '8px !important',
        padding: theme.spacing(3),
        background: isGraded ? theme.palette.ensightful.success[50] : theme.palette.ensightful.neutral[100],
        '&:focus-visible': {
            background: isGraded ? theme.palette.ensightful.success[50] : theme.palette.ensightful.neutral[100],
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
            position: 'relative',
            bottom: '2px',
        },
    };
});

const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        margin: 0,
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    };
});

const OverflowStack = styled(Stack)(({ theme, device }) => {
    return {
        flexDirection: device === 'mobile' ? 'column' : 'row',
        flexWrap: 'unset',
        overflowX: 'auto',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomFormLabel = styled(FormLabel)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '240px',
        minHeight: '100%',
        padding: theme.spacing(3),
        cursor: 'pointer',
    };
});

const CustomRadio = styled(Radio)(({ theme }) => {
    return {
        alignSelf: 'flex-start',
        margin: theme.spacing(-2),
    };
});

const GradingInstructionsAccordion = styled(Accordion)(() => {
    return {
        background: 'inherit',
    };
});

const GradingInstructionsAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        width: 'fit-content',
        '& > div': {
            color: theme.palette.ensightful.primary[500],
        },
    };
});

const GradingInstructionsAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
    };
});

export default forwardRef(
    ({ showGraded, disabled, criteria, form, selectedCriterionId, setSelectedCriterionId, onChange }, ref) => {
        const { device } = useViewport();

        const handleChange = (criterionId, optionId) => {
            onChange(criterionId, optionId);
        };

        useEffect(() => {
            if (device === 'mobile') return;
            criteria
                .map(({ options }) => options)
                .flat()
                .filter(({ suggested }) => suggested)
                .map(({ id }) => id)
                .forEach((id) => {
                    const child = document.getElementById(`${id}-label`);
                    const parent = child.parentElement;
                    const isParentScrollable = parent.scrollWidth > parent.clientWidth;
                    if (isParentScrollable) {
                        const scrollAmount = child.getBoundingClientRect().left - parent.getBoundingClientRect().left;
                        parent.scrollLeft += scrollAmount;
                    }
                });
        }, [device, criteria]);

        return (
            <RootStack ref={ref} spacing={4}>
                {criteria.map((criterion, criterionIndex) => {
                    const criterionSuggestedScore = criterion.options.find(({ suggested }) => suggested)?.rate;
                    const criterionSelectedScore = !form[criterion.id]
                        ? null
                        : Number(criterion.options.find(({ id }) => id === form[criterion.id]).rate);
                    const criterionMaxPossibleScore = Math.max(...criterion.options.map(({ rate }) => Number(rate)));
                    return (
                        <CustomAccordion
                            key={criterion.id}
                            expanded={selectedCriterionId === criterion.id}
                            isGraded={showGraded && Boolean(form[criterion.id])}
                            onChange={() => setSelectedCriterionId(criterion.id)}
                        >
                            <CustomAccordionSummary
                                isExpanded={selectedCriterionId === criterion.id}
                                isGraded={showGraded && Boolean(form[criterion.id])}
                            >
                                <Stack sx={{ width: '100%' }} spacing={2}>
                                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                        <Text>{criterion.title}</Text>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            {showGraded && Boolean(form[criterion.id]) && (
                                                <Tag sx={{ my: -1 }} type={'general'} value={'graded'} />
                                            )}
                                            <Text>
                                                Pts:{' '}
                                                {criterionSelectedScore === null ? (
                                                    <>
                                                        {criterionSuggestedScore ? (
                                                            <Text
                                                                color={'primary'}
                                                                component={'span'}
                                                                tooltip={{ component: 'Suggested score' }}
                                                            >
                                                                {criterionSuggestedScore}
                                                            </Text>
                                                        ) : (
                                                            '--'
                                                        )}
                                                    </>
                                                ) : (
                                                    criterionSelectedScore
                                                )}
                                                /{criterionMaxPossibleScore}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                    <ViewMore variant={'p3'} text={criterion.description} />
                                </Stack>
                            </CustomAccordionSummary>
                            <CustomAccordionDetails>
                                <Stack divider={<Divider />}>
                                    <Stack
                                        sx={(theme) => ({ p: 3, background: theme.palette.ensightful.primary[50] })}
                                        spacing={2}
                                    >
                                        <Text>Why was this grade suggested?</Text>
                                        <Text variant={'p3'}>{criterion.justification}</Text>
                                        {criterion.gradingInstructions && (
                                            <GradingInstructionsAccordion>
                                                <GradingInstructionsAccordionSummary>
                                                    <Text color={'inherit'}>Grading Instructions</Text>
                                                </GradingInstructionsAccordionSummary>
                                                <GradingInstructionsAccordionDetails>
                                                    <Text rich variant={'p3'}>
                                                        {criterion.gradingInstructions}
                                                    </Text>
                                                </GradingInstructionsAccordionDetails>
                                            </GradingInstructionsAccordion>
                                        )}
                                    </Stack>
                                    <OverflowStack
                                        aria-label={`select a grade for criterion ${criterionIndex + 1}`}
                                        component={RadioGroup}
                                        device={device}
                                        divider={
                                            <Divider
                                                flexItem
                                                orientation={device === 'mobile' ? 'horizontal' : 'vertical'}
                                            />
                                        }
                                        tabIndex={0}
                                    >
                                        {criterion.options.map((option) => (
                                            <CustomFormLabel
                                                key={option.id}
                                                id={`${option.id}-label`}
                                                htmlFor={option.id}
                                            >
                                                <Stack spacing={3}>
                                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                                        <Stack
                                                            sx={{ minWidth: 0 }}
                                                            alignItems={'flex-start'}
                                                            spacing={3}
                                                        >
                                                            <Text data-cy={`rubric-points-${option.rate}`}>
                                                                {option.rate} pts
                                                            </Text>
                                                            <Text>{option.title}</Text>
                                                        </Stack>
                                                        <Stack direction={'row'} spacing={2}>
                                                            {option.suggested && (
                                                                <Tag type={'general'} value={'suggested'} />
                                                            )}
                                                            <CustomRadio
                                                                id={option.id}
                                                                checked={form?.[criterion.id] === option.id}
                                                                disabled={disabled}
                                                                onChange={() => handleChange(criterion.id, option.id)}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                    <Text variant={'p3'}>{option.description}</Text>
                                                </Stack>
                                            </CustomFormLabel>
                                        ))}
                                    </OverflowStack>
                                </Stack>
                            </CustomAccordionDetails>
                        </CustomAccordion>
                    );
                })}
            </RootStack>
        );
    }
);
